<template>
  <div class="pay-fee-batch-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('payFeeBatch.list.title')" :subtitle="$t('payFeeBatch.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-refresh-left" @click="_resetPayFeeBatchMethod">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="payFeeBatchInfo.conditions.batchId" clearable class="w-full"
            :placeholder="$t('payFeeBatch.search.batchId')" @keyup.enter.native="_queryPayFeeBatchMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="payFeeBatchInfo.conditions.state" clearable class="w-full"
            :placeholder="$t('payFeeBatch.search.state')">
            <el-option :label="$t('payFeeBatch.state.normal')" value="2006001" />
            <el-option :label="$t('payFeeBatch.state.applyCancel')" value="2007001" />
            <el-option :label="$t('payFeeBatch.state.auditPass')" value="2008001" />
            <el-option :label="$t('payFeeBatch.state.auditFail')" value="2009001" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="payFeeBatchInfo.conditions.createUserName" clearable class="w-full"
            :placeholder="$t('payFeeBatch.search.createUserName')" @keyup.enter.native="_queryPayFeeBatchMethod" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="_queryPayFeeBatchMethod">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="_resetPayFeeBatchMethod">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_listPayFeeBatchs(pagination.current, pagination.size)">
        <template #left>
          <span class="ds-panel__title">{{ $t('payFeeBatch.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="payFeeBatchInfo.payFeeBatchs" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('payFeeBatch.page.empty')"
        @retry="_listPayFeeBatchs(pagination.current, pagination.size)">
        <el-table-column prop="batchId" :label="$t('payFeeBatch.table.batchId')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.batchId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createUserName" :label="$t('payFeeBatch.table.createUserName')" align="center" width="120">
          <template slot-scope="scope">
            {{ scope.row.createUserName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('payFeeBatch.table.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('payFeeBatch.table.remark')" align="left" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="ds-text-muted">{{ scope.row.remark || $t('payFeeBatch.table.noRemark') }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('payFeeBatch.table.stateName')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <el-table-column prop="msg" :label="$t('payFeeBatch.table.msg')" align="left" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="ds-text-muted">{{ scope.row.msg || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" fixed="right" width="160">
          <template slot-scope="scope">
            <el-button v-if="scope.row.state === '2006001'" size="mini" type="text" @click="_openApply(scope.row)">
              {{ $t('payFeeBatch.button.applyCancel') }}
            </el-button>
            <el-button v-if="scope.row.state === '2007001'" size="mini" type="text" @click="_openPayFeeBatchAuditModel(scope.row)">
              {{ $t('payFeeBatch.button.audit') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('payFeeBatch.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <apply-delete-fee-batch ref="applyDeleteFeeBatch" @success="handleSuccess" />
    <audit ref="audit" @success="_auditPayFeeBatchState" />
  </div>
</template>

<script>
import { listPayFeeBatch, updatePayFeeBatch } from '@/api/fee/payFeeBatchApi'
import { getCommunityId } from '@/api/community/communityApi'
import ApplyDeleteFeeBatch from '@/components/fee/applyDeleteFeeBatch'
import Audit from '@/components/fee/audit'
import { formatDateTimeSec } from '@/utils/format'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'PayFeeBatchList',
  components: {
    ApplyDeleteFeeBatch,
    Audit,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      payFeeBatchInfo: {
        payFeeBatchs: [],
        conditions: {
          communityId: '',
          state: '',
          batchId: '',
          createUserName: ''
        }
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.payFeeBatchInfo.conditions.communityId = getCommunityId()
    this._listPayFeeBatchs(this.pagination.current, this.pagination.size)
  },
  methods: {
    async _listPayFeeBatchs(page, size) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.payFeeBatchInfo.conditions,
          page,
          row: size
        }
        const { data, total } = await listPayFeeBatch(params)
        this.payFeeBatchInfo.payFeeBatchs = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取数据失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('payFeeBatch.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    _queryPayFeeBatchMethod() {
      this.pagination.current = 1
      this._listPayFeeBatchs(this.pagination.current, this.pagination.size)
    },
    _resetPayFeeBatchMethod() {
      this.payFeeBatchInfo.conditions = {
        ...this.payFeeBatchInfo.conditions,
        state: '',
        batchId: '',
        createUserName: ''
      }
      this._queryPayFeeBatchMethod()
    },
    _openApply(payFee) {
      this.$refs.applyDeleteFeeBatch.open(payFee)
    },
    _openPayFeeBatchAuditModel(payFee) {
      this.payFeeBatchInfo.payFeeBatch = payFee
      this.$refs.audit.open()
    },
    async _auditPayFeeBatchState(auditInfo) {
      try {
        const payFeeBatch = {
          ...this.payFeeBatchInfo.payFeeBatch,
          state: auditInfo.state,
          msg: auditInfo.remark
        }
        await updatePayFeeBatch(payFeeBatch)
        this.$message.success(this.$t('common.operationSuccess'))
        this._listPayFeeBatchs(this.pagination.current, this.pagination.size)
      } catch (error) {
        console.error('审核失败:', error)
      }
    },
    handleSuccess() {
      this._listPayFeeBatchs(this.pagination.current, this.pagination.size)
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this._listPayFeeBatchs(this.pagination.current, this.pagination.size)
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this._listPayFeeBatchs(this.pagination.current, this.pagination.size)
    },
    /**
     * 批量取消费用状态：2006001 正常 / 2007001 申请取消 / 2008001 审核通过 / 2009001 审核失败
     */
    resolveState(row) {
      if (!row) return 'muted'
      const map = {
        2006001: 'muted',
        2007001: 'warning',
        2008001: 'success',
        2009001: 'danger'
      }
      const key = row.state === null || row.state === undefined ? '' : String(row.state)
      if (map[key]) return map[key]
      const resolved = resolveStatus(row.state, {
        text: row.stateName || '',
        fallbackLabel: row.stateName || ''
      })
      return resolved.type
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.pay-fee-batch-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 辅助文本 */
.ds-text-muted {
  color: var(--ds-gray-500);
}
</style>
